import { useEffect, useRef } from 'react'
import i18next from 'i18next'
import { useAuthStore } from '@/store/authStore'
import { useUIStore } from '@/store/uiStore'
import { userApi } from '@/api/endpoints'

/**
 * Syncs user preferences (language + theme) from the server on app mount.
 *
 * Flow:
 * 1. On mount, if authenticated, fetch GET /user/profile
 * 2. If server language differs from current i18next language, apply it
 * 3. If server theme differs from current UI store theme, apply it
 * 4. Update cached user in auth store so future rehydrations are correct
 *
 * Runs once per app lifecycle (guarded by ref). Silent on failure.
 * Must be called inside a component that renders AFTER auth hydration.
 */
export function useLanguageSync() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const hasHydrated = useAuthStore((s) => s._hasHydrated)
  const updateUser = useAuthStore((s) => s.updateUser)
  const syncedRef = useRef(false)

  useEffect(() => {
    if (!hasHydrated || !isAuthenticated || syncedRef.current) return
    syncedRef.current = true

    userApi.getProfile().then((profile) => {
      // Sync language — update both i18next AND UI store so the toggle reflects correctly
      if (profile.language) {
        if (profile.language !== i18next.language) {
          i18next.changeLanguage(profile.language)
          document.documentElement.lang = profile.language
        }
        const { language: currentLang } = useUIStore.getState()
        if (profile.language !== currentLang) {
          useUIStore.setState({ language: profile.language })
        }
      }

      // Sync theme — update CSS class AND UI store so the toggle reflects correctly
      const { theme: currentTheme } = useUIStore.getState()
      if (profile.theme && profile.theme !== currentTheme) {
        const root = window.document.documentElement
        root.classList.remove('light', 'dark')
        if (profile.theme === 'system') {
          const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
          root.classList.add(systemTheme)
        } else {
          root.classList.add(profile.theme)
        }
        useUIStore.setState({ theme: profile.theme })
      }

      updateUser(profile)
    }).catch(() => {
      // Silent — cached preferences are good enough
    })
  }, [hasHydrated, isAuthenticated, updateUser])
}
